import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { connectIntegration } from "../api/mutations";
import Drawer from "../blocks/drawer";
import {
  generateClientId,
  generateClientSecret,
} from "../utils/credentials";
import { WEBHOOK_EVENT_OPTIONS } from "../constants/webhookEvents";

// Integration type to key mapping
const INTEGRATION_TYPE_MAP = {
  1: { key: "twilio", name: "Twilio" },
  2: { key: "sendgrid", name: "SendGrid" },
  3: { key: "mailchimp", name: "Mailchimp" },
  4: { key: "mailgun", name: "Mailgun" },
  7: { key: "shopify", name: "Shopify" },
  11: { key: "mandrill", name: "Mandrill" },
  10: { key: "brevo", name: "Brevo" },
  12: { key: "webhook", name: "Webhook" },
  13: { key: "bookingkit", name: "BookingKit" },
};

const IntegrationManageModal = ({
  isOpen,
  onClose,
  integrationType,
  integrationData,
  storeId,
  onSuccess,
  integrationList,
}) => {
  const { t } = useLanguage();
  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [api_url, setApi_url] = useState("");
  const [shopDomain, setShopDomain] = useState("");
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookAuthType, setWebhookAuthType] = useState("none");
  const [webhookAuthToken, setWebhookAuthToken] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Get the current integration details including createApiKeyLink
  const currentIntegration = React.useMemo(() => {
    return integrationList?.find(
      (item) => item.key === INTEGRATION_TYPE_MAP[integrationType]?.key,
    );
  }, [integrationList, integrationType]);

  // Set initial values if integration is already connected
  React.useEffect(() => {
    if (integrationData && integrationType === 1) {
      setAccountSid(integrationData.accountSid);
      setAuthToken(integrationData.authToken);
      setApiKey("");
      setApi_url("");
      setShopDomain("");
    } else if (integrationData && integrationType === 2) {
      setAccountSid("");
      setAuthToken("");
      setApiKey(integrationData.apiKey);
      setApi_url("");
      setShopDomain("");
    } else if (integrationData && integrationType === 3) {
      setAccountSid("");
      setAuthToken("");
      setApiKey(integrationData.apiKey);
      setApi_url("");
      setShopDomain("");
    } else if (integrationData && integrationType === 4) {
      setAccountSid("");
      setAuthToken("");
      setApiKey(integrationData.apiKey);
      setApi_url(integrationData.apiUrl);
      setShopDomain("");
    } else if (integrationData && integrationType === 11) {
      setAccountSid("");
      setAuthToken("");
      setApiKey(integrationData.apiKey);
      setApi_url("");
      setShopDomain("");
    } else if (integrationData && integrationType === 10) {
      setAccountSid("");
      setAuthToken("");
      setApiKey(integrationData.apiKey);
      setApi_url("");
      setShopDomain("");
    } else if (integrationData && integrationType === 7) {
      setAccountSid("");
      setAuthToken("");
      setApiKey("");
      setApi_url("");
      setShopDomain(integrationData.shopDomain);
      setWebhookUrl("");
      setWebhookEvents([]);
    } else if (integrationData && integrationType === 12) {
      setAccountSid("");
      setAuthToken("");
      setApiKey("");
      setApi_url("");
      setShopDomain("");
      setWebhookUrl(integrationData.url || "");
      setWebhookEvents(integrationData.events || []);
      setWebhookAuthType(integrationData.auth_type || "none");
      setWebhookAuthToken(integrationData.auth_token || "");
    } else {
      setAccountSid("");
      setAuthToken("");
      setApiKey("");
      setApi_url("");
      setShopDomain("");
      setWebhookUrl("");
      setWebhookEvents([]);
      setWebhookAuthType("none");
      setWebhookAuthToken("");
    }

    // Clear error message when modal is opened or closed
    setErrorMessage("");
    setIsLoading(false);
  }, [integrationData, integrationType, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setErrorMessage(""); // Clear any previous errors

      // Find the integration in the list to get its tags
      const integration = integrationList?.find(
        (item) => item.key === INTEGRATION_TYPE_MAP[integrationType]?.key,
      );

      let integrationPayload = {
        integration_type: integrationType,
        config: {},
        tags: integration?.tags || [],
      };

      // Configure based on integration type
      switch (integrationType) {
        case 1: // Twilio
          integrationPayload.config = {
            accountSid: accountSid,
            authToken: authToken,
          };
          break;
        case 2: // Sendgrid
          integrationPayload.config = {
            apiKey: apiKey,
          };
          break;
        case 3: // Mailchimp
          integrationPayload.config = {
            apiKey: apiKey,
          };
          break;
        case 4: // Mailgun
          integrationPayload.config = {
            apiKey: apiKey,
            apiUrl: api_url,
          };
          break;
        case 11: // Mandrill
          integrationPayload.config = {
            apiKey: apiKey,
          };
          break;
        case 10: // Brevo
          integrationPayload.config = {
            apiKey: apiKey,
          };
          break;
        case 7: // Shopify
          integrationPayload.config = {
            shopDomain: shopDomain,
          };
          break;
        case 12: // Webhook
          integrationPayload.config = {
            url: webhookUrl,
            events: webhookEvents,
            auth_type: webhookAuthType,
            ...(webhookAuthType === "bearer" && { auth_token: webhookAuthToken }),
          };
          break;
        case 13: // BookingKit
          integrationPayload.config = {
            enabled: true,
            oauthClientId: generateClientId(),
            oauthClientSecret: generateClientSecret(),
            allowedVendorIds: [],
            allowedProductIds: [],
            eligibleStatuses: ["ACCEPTED"],
            expectedCurrency: "EUR",
            claimGracePeriodDays: 7,
          };
          break;
      }

      const data = await connectIntegration({
        storeId: storeId,
        integrationData: integrationPayload,
      });

      // Reset form and close modal
      setAccountSid("");
      setAuthToken("");
      setApiKey("");
      setApi_url("");
      setShopDomain("");
      setWebhookUrl("");
      setWebhookEvents([]);
      setWebhookAuthType("none");
      setWebhookAuthToken("");
      setErrorMessage("");
      onClose();

      // Notify parent component of success
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          "Failed to connect integration. Please check your credentials and try again.",
      );
      setIsLoading(false);
    }
  };

  const getModalTitle = () => {
    return (
      t(
        `integrations.form.${INTEGRATION_TYPE_MAP[integrationType]?.key}Setup`,
      ) || t("integrations.setup")
    );
  };

  const getModalDescription = () => {
    const integrationName = INTEGRATION_TYPE_MAP[integrationType]?.name || "";
    return t("integrations.form.createDescription", { integrationName });
  };

  const renderFormFields = () => {
    // Define integration-specific fields
    const integrationFields = {
      1: [
        // Twilio
        {
          id: "accountSid",
          label: "Account SID",
          type: "text",
          value: accountSid,
          onChange: (e) => setAccountSid(e.target.value),
          placeholder: "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
          required: !integrationData,
          disabled: integrationData && integrationType === 1,
          isSecret: false,
        },
        {
          id: "authToken",
          label: "Auth Token",
          type: showAuthToken ? "text" : "password",
          value: authToken,
          onChange: (e) => setAuthToken(e.target.value),
          required: !integrationData,
          disabled: integrationData && integrationType === 1,
          isSecret: true,
        },
      ],
      2: [
        // SendGrid
        {
          id: "apiKey",
          label: "API Key",
          type: showApiKey ? "text" : "password",
          value: apiKey,
          onChange: (e) => setApiKey(e.target.value),
          required: !integrationData,
          disabled: integrationData && integrationType === 2,
          isSecret: true,
        },
      ],
      3: [
        // Mailchimp
        {
          id: "apiKey",
          label: "API Key",
          type: showApiKey ? "text" : "password",
          value: apiKey,
          onChange: (e) => setApiKey(e.target.value),
          required: !integrationData,
          disabled: integrationData && integrationType === 3,
          isSecret: true,
        },
      ],
      4: [
        // Mailgun
        {
          id: "api_url",
          label: "Region",
          type: "select",
          value: api_url,
          onChange: (e) => setApi_url(e.target.value),
          required: !integrationData,
          disabled: integrationData && integrationType === 4,
          isSecret: false,
          options: [
            {
              value: "",
              label: t("integrations.form.selectRegion"),
            },
            {
              value: "api.mailgun.net",
              label: t("integrations.regions.global"),
            },
            {
              value: "api.eu.mailgun.net",
              label: t("integrations.regions.eu"),
            },
          ],
        },
        {
          id: "apiKey",
          label: "API Key",
          type: showApiKey ? "text" : "password",
          value: apiKey,
          onChange: (e) => setApiKey(e.target.value),
          required: !integrationData,
          disabled: integrationData && integrationType === 4,
          isSecret: true,
        },
      ],
      11: [
        // Mandrill
        {
          id: "apiKey",
          label: "API Key",
          type: showApiKey ? "text" : "password",
          value: apiKey,
          onChange: (e) => setApiKey(e.target.value),
          required: !integrationData,
          disabled: integrationData && integrationType === 11,
          isSecret: true,
        },
      ],
      10: [
        // Brevo
        {
          id: "apiKey",
          label: "API Key",
          type: showApiKey ? "text" : "password",
          value: apiKey,
          onChange: (e) => setApiKey(e.target.value),
          required: !integrationData,
          disabled: integrationData && integrationType === 10,
          isSecret: true,
        },
      ],
      7: [
        // Shopify
        {
          id: "shopDomain",
          label: "Shop Domain",
          type: "text",
          value: shopDomain,
          onChange: (e) => setShopDomain(e.target.value),
          placeholder: "your-store.myshopify.com",
          required: !integrationData,
          disabled: integrationData && integrationType === 7,
          isSecret: false,
        },
      ],
      12: [
        // Webhook
        {
          id: "webhookUrl",
          label: t("integrations.webhook.endpointUrl"),
          type: "text",
          value: webhookUrl,
          onChange: (e) => setWebhookUrl(e.target.value),
          placeholder: "https://example.com/webhook",
          required: true,
          disabled: false,
          isSecret: false,
        },
      ],
    };

    // Get fields for current integration type
    const fields = integrationFields[integrationType] || [];

    // Render fields
    return fields.map((field) => (
      <div key={field.id}>
        <div className="flex items-center">
          <label
            htmlFor={field.id}
            className="block text-sm font-medium text-gray-700"
          >
            {field.label}
          </label>
          {(field.id === "apiKey" ||
            (field.id === "authToken" && integrationType === 1) ||
            (field.id === "adminApiAccessToken" && integrationType === 7)) &&
            currentIntegration?.createApiKeyLink && (
              <a
                href={currentIntegration.createApiKeyLink}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 text-gray-400 hover:text-gray-500"
                title="Learn how to create API key"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                  />
                </svg>
              </a>
            )}
        </div>
        <div className={`${field.isSecret ? "relative" : ""} mt-1`}>
          {field.type === "select" ? (
            <select
              id={field.id}
              value={field.value}
              onChange={field.onChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required={field.required}
              disabled={field.disabled}
            >
              {field.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={field.type}
              id={field.id}
              value={field.value}
              onChange={field.onChange}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${field.isSecret ? "pr-10" : ""}`}
              placeholder={field.placeholder}
              required={field.required}
              disabled={field.disabled}
            />
          )}
          {field.isSecret && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => {
                if (field.id === "authToken") {
                  setShowAuthToken(!showAuthToken);
                } else if (field.id === "apiKey") {
                  setShowApiKey(!showApiKey);
                }
              }}
            >
              {(field.id === "authToken" && showAuthToken) ||
              (field.id === "apiKey" && showApiKey) ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5 text-gray-500"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5 text-gray-500"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                  />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
    ));
  };

  return (
    <Drawer
      open={isOpen}
      setOpen={onClose}
      header={<>{getModalTitle()}</>}
      onSubmit={handleSubmit}
      submitText={t("integrations.connect")}
      submitDisabled={isLoading || (integrationType === 12 && webhookEvents.length === 0)}
      isLoading={isLoading}
      cancelText={t("common.cancel")}
    >
      <div className="px-4 py-6 sm:px-6">
        {errorMessage && (
          <div className="mb-6">
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <div className="text-sm text-red-700">
                    <p>{errorMessage}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">{renderFormFields()}</div>

        {integrationType === 13 && (
          <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800 mt-2">
            {t("integrations.bookingkit.setupHint") ||
              "BookingKit will be connected with empty settings. Open the integration's configuration page to set the webhook secret, loyalty card, vendor IDs and product IDs."}
          </div>
        )}

        {integrationType === 12 && (
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("integrations.webhook.authorization")}
              </label>
              <select
                value={webhookAuthType}
                onChange={(e) => {
                  setWebhookAuthType(e.target.value);
                  if (e.target.value === "none") setWebhookAuthToken("");
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="none">{t("integrations.webhook.authNone")}</option>
                <option value="bearer">{t("integrations.webhook.authBearer")}</option>
              </select>
            </div>
            {webhookAuthType === "bearer" && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t("integrations.webhook.bearerToken")}
                </label>
                <input
                  type="password"
                  value={webhookAuthToken}
                  onChange={(e) => setWebhookAuthToken(e.target.value)}
                  placeholder="your-secret-token"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  required
                />
              </div>
            )}
          </div>
        )}

        {integrationType === 12 && (
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t("integrations.webhook.selectEvents")}
            </label>
            <div className="space-y-3">
              {WEBHOOK_EVENT_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center space-x-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={webhookEvents.includes(option.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setWebhookEvents([...webhookEvents, option.value]);
                      } else {
                        setWebhookEvents(
                          webhookEvents.filter((ev) => ev !== option.value),
                        );
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">
                    {t(option.labelKey)}
                  </span>
                </label>
              ))}
            </div>
            {webhookEvents.length === 0 && (
              <p className="mt-2 text-xs text-red-500">
                {t("integrations.webhook.selectAtLeastOne")}
              </p>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default IntegrationManageModal;
